import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper function to add a table to the PDF
const addTable = (pdf, headers, rows, startY, pageWidth) => {
  const cellPadding = 2;
  const cellHeight = 8;
  const fontSize = 7;
  const headerFontSize = 8;
  const marginLeft = 15;
  const tableWidth = pageWidth - 30;
  const cellWidth = tableWidth / headers.length;

  let currentY = startY;

  // Draw header row
  pdf.setFillColor(26, 76, 58);
  pdf.rect(marginLeft, currentY, tableWidth, cellHeight + 2, 'F');

  pdf.setFontSize(headerFontSize);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont(undefined, 'bold');

  headers.forEach((header, i) => {
    pdf.text(header, marginLeft + (i * cellWidth) + cellPadding, currentY + cellHeight - 1);
  });

  currentY += cellHeight + 2;

  // Draw data rows
  pdf.setFontSize(fontSize);
  pdf.setFont(undefined, 'normal');

  rows.forEach((row, rowIndex) => {
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      pdf.setFillColor(248, 248, 248);
      pdf.rect(marginLeft, currentY, tableWidth, cellHeight, 'F');
    }

    pdf.setTextColor(51, 51, 51);
    row.forEach((cell, cellIndex) => {
      const cellText = String(cell || 'N/A').substring(0, 15);
      pdf.text(cellText, marginLeft + (cellIndex * cellWidth) + cellPadding, currentY + cellHeight - 2);
    });

    currentY += cellHeight;
  });

  // Draw border
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.2);
  pdf.rect(marginLeft, startY, tableWidth, currentY - startY, 'S');

  return currentY + 5;
};

// LOS color mapping
const getLosColor = (los) => {
  const colors = {
    'A': [143, 201, 117],
    'B': [174, 211, 97],
    'C': [246, 237, 96],
    'D': [253, 182, 78],
    'E': [236, 29, 35],
    'F': [71, 13, 22]
  };
  return colors[los] || [150, 150, 150];
};

// Vehicle type colors for charts
const vehicleTypeColors = {
  totale: [22, 163, 74],   // Green
  car: [37, 99, 235],      // Blue
  truck: [234, 88, 12],    // Orange
  brt: [147, 51, 234],     // Purple
};

// Helper to normalize IDs (handles "455.0" vs 455 format)
const normalizeId = (id) => {
  if (id === null || id === undefined) return null;
  const parsed = typeof id === 'string' ? parseFloat(id) : id;
  return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
};

// Draw a line chart in PDF
const drawChart = (pdf, chartData, title, unit, startX, startY, chartWidth, chartHeight, color = [16, 185, 129]) => {
  if (!chartData || chartData.length === 0) {
    // Draw empty chart placeholder
    pdf.setFillColor(248, 248, 248);
    pdf.roundedRect(startX, startY, chartWidth, chartHeight, 2, 2, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Nessun dato disponibile', startX + chartWidth / 2, startY + chartHeight / 2, { align: 'center' });
    return startY + chartHeight + 5;
  }

  // Detect vehicle types in data first (needed for range calculation)
  const vehicleTypes = [];
  if (chartData.length > 0) {
    const first = chartData[0];
    if (typeof first.totale !== 'undefined') vehicleTypes.push({ key: 'totale', name: 'Totale', color: vehicleTypeColors.totale });
    if (typeof first.car !== 'undefined') vehicleTypes.push({ key: 'car', name: 'Car', color: vehicleTypeColors.car });
    if (typeof first.truck !== 'undefined') vehicleTypes.push({ key: 'truck', name: 'Truck', color: vehicleTypeColors.truck });
    if (typeof first.brt !== 'undefined') vehicleTypes.push({ key: 'brt', name: 'BRT', color: vehicleTypeColors.brt });
  }

  // Title
  pdf.setFontSize(9);
  pdf.setTextColor(51, 51, 51);
  pdf.setFont(undefined, 'bold');
  pdf.text(title, startX, startY);
  startY += 5;

  // Chart area - increase height if we have vehicle types to show legend
  const hasVehicleTypes = vehicleTypes.length > 0;
  const chartAreaHeight = chartHeight - (hasVehicleTypes ? 20 : 15);
  const chartAreaWidth = chartWidth - 10;
  const chartStartX = startX + 8;
  const chartStartY = startY;

  // Draw background
  pdf.setFillColor(252, 252, 252);
  pdf.rect(chartStartX, chartStartY, chartAreaWidth, chartAreaHeight, 'F');

  // Draw grid lines
  pdf.setDrawColor(230, 230, 230);
  pdf.setLineWidth(0.1);
  for (let i = 0; i <= 4; i++) {
    const y = chartStartY + (chartAreaHeight * i / 4);
    pdf.line(chartStartX, y, chartStartX + chartAreaWidth, y);
  }

  // Get data range - calculate from ALL vehicle types
  let allValues = [];
  if (vehicleTypes.length > 0) {
    vehicleTypes.forEach(vt => {
      chartData.forEach(d => {
        const val = d[vt.key];
        if (val !== undefined && val !== null && !isNaN(val)) {
          allValues.push(val);
        }
      });
    });
  }
  // Fallback to 'value' if no vehicle types
  if (allValues.length === 0) {
    allValues = chartData.map(d => d.value || 0).filter(v => !isNaN(v));
  }

  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 1;
  const minValue = allValues.length > 0 ? Math.min(...allValues, 0) : 0;
  const range = maxValue - minValue || 1;

  // Draw lines for each vehicle type
  const drawLine = (dataKey, lineColor) => {
    pdf.setDrawColor(...lineColor);
    pdf.setLineWidth(0.5);

    const sortedData = [...chartData].sort((a, b) => a.time - b.time);
    let prevX = null;
    let prevY = null;

    sortedData.forEach((point, index) => {
      const value = point[dataKey] ?? point.value ?? 0;
      const x = chartStartX + (index / (sortedData.length - 1 || 1)) * chartAreaWidth;
      const y = chartStartY + chartAreaHeight - ((value - minValue) / range) * chartAreaHeight;

      if (prevX !== null && prevY !== null) {
        pdf.line(prevX, prevY, x, y);
      }

      prevX = x;
      prevY = y;
    });
  };

  if (vehicleTypes.length > 0) {
    // Draw lines for all vehicle types
    vehicleTypes.forEach(vt => {
      drawLine(vt.key, vt.color);
    });

    // Draw legend for all vehicle types
    let legendX = chartStartX;
    const legendY = chartStartY + chartAreaHeight + 5;
    pdf.setFontSize(6);
    vehicleTypes.forEach(vt => {
      pdf.setFillColor(...vt.color);
      pdf.rect(legendX, legendY - 2, 4, 2, 'F');
      pdf.setTextColor(80, 80, 80);
      pdf.text(vt.name, legendX + 5, legendY);
      legendX += 18;
    });
  } else {
    // Draw single line with default color when no vehicle types detected
    drawLine('value', color);
  }

  // Y-axis labels
  pdf.setFontSize(6);
  pdf.setTextColor(120, 120, 120);
  pdf.text(maxValue.toFixed(1), startX, chartStartY + 3);
  pdf.text(minValue.toFixed(1), startX, chartStartY + chartAreaHeight);

  // X-axis labels (first and last time)
  if (chartData.length > 0) {
    const sortedData = [...chartData].sort((a, b) => a.time - b.time);
    const xLabelY = hasVehicleTypes ? chartStartY + chartAreaHeight + 12 : chartStartY + chartAreaHeight + 8;
    pdf.text(String(sortedData[0].time), chartStartX, xLabelY);
    pdf.text(String(sortedData[sortedData.length - 1].time), chartStartX + chartAreaWidth - 5, xLabelY);
  }

  // Unit label
  pdf.setFontSize(7);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`(${unit})`, startX + chartWidth - 15, startY - 3);

  return startY + chartHeight;
};

// Draw a 2x2 grid of charts
const drawChartsGrid = (pdf, chartData, startY, pageWidth, pageHeight) => {
  const marginLeft = 15;
  const marginRight = 15;
  const chartWidth = (pageWidth - marginLeft - marginRight - 10) / 2;
  const chartHeight = 55; // Increased height to accommodate legends

  // Check if we need a new page
  if (startY + chartHeight * 2 + 25 > pageHeight) {
    pdf.addPage();
    startY = 20;
  }

  // Row 1: Veicoli and Coda Max
  const row1Y = startY;
  drawChart(pdf, chartData?.veicoli || [], 'Veicoli', 'veh', marginLeft, row1Y, chartWidth, chartHeight, [16, 185, 129]);
  drawChart(pdf, chartData?.codaMax || [], 'Coda Max', 'veh', marginLeft + chartWidth + 10, row1Y, chartWidth, chartHeight, [59, 130, 246]);

  // Row 2: Tempo and Velocita
  const row2Y = row1Y + chartHeight + 12;
  drawChart(pdf, chartData?.tempo || [], 'Tempo Medio', 'sec', marginLeft, row2Y, chartWidth, chartHeight, [245, 158, 11]);
  drawChart(pdf, chartData?.velocita || [], 'Velocità Media', 'km/h', marginLeft + chartWidth + 10, row2Y, chartWidth, chartHeight, [99, 102, 241]);

  return row2Y + chartHeight + 12;
};

// Process chart data from graficiDataJson for a specific section
const getChartDataForSection = (graficiDataJson, scenarioId, sectionId, timeInterval = '10min') => {
  if (!graficiDataJson?.scenario) return null;

  const scenario = graficiDataJson.scenario.find(s => s.replicaAvg === scenarioId);
  if (!scenario?.sezioni) return null;

  const normalizedSectionId = normalizeId(sectionId);
  const sectionData = scenario.sezioni.find(s => normalizeId(s.id) === normalizedSectionId);

  if (!sectionData?.serieTemporale?.inputCount) return null;

  const timeKey = timeInterval;
  const inputData = sectionData.serieTemporale.inputCount?.[timeKey] || sectionData.serieTemporale.inputCount?.['10min'];
  const qmaxData = sectionData.serieTemporale.qmax?.[timeKey] || sectionData.serieTemporale.qmax?.['10min'];
  const speedData = sectionData.serieTemporale.speed?.[timeKey] || sectionData.serieTemporale.speed?.['10min'];
  const ttimeData = sectionData.serieTemporale.ttime?.[timeKey] || sectionData.serieTemporale.ttime?.['10min'];

  const processDataPoints = (sourceData) => {
    if (!sourceData) return [];
    const result = [];
    const vehicleTypes = Object.keys(sourceData);
    if (vehicleTypes.length === 0) return result;

    const firstType = vehicleTypes[0];
    const timeKeys = Object.keys(sourceData[firstType] || {});

    timeKeys.forEach(tk => {
      const time = parseFloat(tk);
      if (!isNaN(time)) {
        const point = { time };
        vehicleTypes.forEach(vType => {
          const value = sourceData[vType]?.[tk];
          if (value !== undefined && value !== null) {
            point[vType.toLowerCase()] = value;
          }
        });
        point.value = point.totale ?? point.car ?? point[vehicleTypes[0].toLowerCase()] ?? 0;
        result.push(point);
      }
    });

    return result.sort((a, b) => a.time - b.time);
  };

  return {
    veicoli: processDataPoints(inputData),
    codaMax: processDataPoints(qmaxData),
    tempo: processDataPoints(ttimeData),
    velocita: processDataPoints(speedData),
    globalIndicators: sectionData.indicatoriGlobali || {}
  };
};

export const generateNodeReportPDF = async (
  nodeData1, nodeData2, isComparison, scenarioId1, scenarioId2,
  dataJson, nodeMetrics1, nodeMetrics2,
  losDataJson = null, reportType = 'scenario',
  roadData1 = null, roadData2 = null,
  chartData1 = null, chartData2 = null,
  graficiDataJson = null
) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const date = new Date().toLocaleDateString('it-IT');

  // Header
  pdf.setFontSize(22);
  pdf.setTextColor(26, 76, 58);
  pdf.text('Traffic Analysis Report', pageWidth / 2, 20, { align: 'center' });

  pdf.setFontSize(10);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Generated: ${date}`, pageWidth / 2, 28, { align: 'center' });

  pdf.setDrawColor(161, 227, 64);
  pdf.setLineWidth(0.5);
  pdf.line(15, 32, pageWidth - 15, 32);

  let yPosition = 42;

  // SCENARIO REPORT - Full tables with charts for all sections
  if (reportType === 'scenario') {
    // Find scenario info
    const scenario1 = dataJson?.scenario?.find(s => s.replicaAvg === scenarioId1);
    const scenario2 = isComparison ? dataJson?.scenario?.find(s => s.replicaAvg === scenarioId2) : null;

    // Title
    pdf.setFontSize(16);
    pdf.setTextColor(26, 76, 58);
    pdf.text('Rapporto Scenario Completo', 15, yPosition);
    yPosition += 12;

    // Scenario info box
    pdf.setFillColor(248, 248, 248);
    pdf.roundedRect(15, yPosition - 5, pageWidth - 30, isComparison ? 35 : 25, 3, 3, 'F');

    pdf.setFontSize(11);
    pdf.setTextColor(51, 51, 51);
    pdf.setFont(undefined, 'bold');
    pdf.text('Scenario 1:', 20, yPosition + 5);
    pdf.setFont(undefined, 'normal');
    pdf.text(scenario1?.scName || `Scenario ${scenarioId1}`, 50, yPosition + 5);

    if (isComparison && scenario2) {
      pdf.setFont(undefined, 'bold');
      pdf.text('Scenario 2:', 20, yPosition + 15);
      pdf.setFont(undefined, 'normal');
      pdf.text(scenario2?.scName || `Scenario ${scenarioId2}`, 50, yPosition + 15);
      yPosition += 40;
    } else {
      yPosition += 30;
    }

    // Global Network Results Section - Use 'sistema' values from graficiDataJson
    pdf.setFontSize(14);
    pdf.setTextColor(26, 76, 58);
    pdf.text('Risultati Rete Globale', 15, yPosition);
    yPosition += 10;

    // Get sistema values from graficiDataJson for each scenario
    const getSistemaValues = (scenarioId) => {
      if (!graficiDataJson?.scenario) return null;
      const scenario = graficiDataJson.scenario.find(s => s.replicaAvg === scenarioId);
      return scenario?.sistema || null;
    };

    const sistema1 = getSistemaValues(scenarioId1);
    const sistema2 = isComparison ? getSistemaValues(scenarioId2) : null;

    // Scenario 1 Table
    pdf.setFontSize(12);
    pdf.setTextColor(51, 51, 51);
    pdf.setFont(undefined, 'bold');
    pdf.text('Scenario 1', 15, yPosition);
    yPosition += 6;

    const globalHeaders1 = ['Metrica', 'Scenario 1'];
    const globalRows1 = [
      ['Flusso (Veic/H)', sistema1?.flowSys !== undefined && sistema1?.flowSys !== null ? String(sistema1.flowSys.toFixed(2)) : 'N/A'],
      ['Coda Media (Veic)', sistema1?.qmeanSys !== undefined && sistema1?.qmeanSys !== null ? String(sistema1.qmeanSys.toFixed(2)) : 'N/A'],
      ['Ritardo Medio (Sec/Km)', sistema1?.dtimeSys !== undefined && sistema1?.dtimeSys !== null ? String(sistema1.dtimeSys.toFixed(2)) : 'N/A']
    ];

    yPosition = addTable(pdf, globalHeaders1, globalRows1, yPosition, pageWidth);

    // Scenario 2 Table (if comparison mode)
    if (isComparison) {
      yPosition += 8;

      pdf.setFontSize(12);
      pdf.setTextColor(51, 51, 51);
      pdf.setFont(undefined, 'bold');
      pdf.text('Scenario 2', 15, yPosition);
      yPosition += 6;

      const globalHeaders2 = ['Metrica', 'Scenario 2'];
      const globalRows2 = [
        ['Flusso (Veic/H)', sistema2?.flowSys !== undefined && sistema2?.flowSys !== null ? String(sistema2.flowSys.toFixed(2)) : 'N/A'],
        ['Coda Media (Veic)', sistema2?.qmeanSys !== undefined && sistema2?.qmeanSys !== null ? String(sistema2.qmeanSys.toFixed(2)) : 'N/A'],
        ['Ritardo Medio (Sec/Km)', sistema2?.dtimeSys !== undefined && sistema2?.dtimeSys !== null ? String(sistema2.dtimeSys.toFixed(2)) : 'N/A']
      ];

      yPosition = addTable(pdf, globalHeaders2, globalRows2, yPosition, pageWidth);
    }

    yPosition += 10;

    // Helper function to add scenario tables
    const addScenarioTables = (scenarioData, scenarioLabel, scenarioNum) => {
      if (!scenarioData?.LosNodi || scenarioData.LosNodi.length === 0) return yPosition;

      // Check if we need a new page for nodes table
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      // Nodes Table
      pdf.setFontSize(14);
      pdf.setTextColor(26, 76, 58);
      pdf.text(`Tabella Nodi ${scenarioLabel}`, 15, yPosition);
      yPosition += 8;

      const nodeHeaders = ['Nome', 'Tipo', 'LOS', 'Ritardo (sec)', 'Veicoli'];
      const nodeRows = scenarioData.LosNodi.slice(0, 15).map(node => {
        let totalVehicles = 0;
        if (node.sezioni) {
          Object.values(node.sezioni).forEach(sec => {
            totalVehicles += sec.count ?? 0;
          });
        }
        return [
          (node.nome ?? "Intersezione").substring(0, 25),
          node.tipo ?? 'N/A',
          node.losNode ?? 'N/A',
          node.delayNode !== undefined && node.delayNode !== null ? node.delayNode.toFixed(1) : 'N/A',
          String(totalVehicles)
        ];
      });

      yPosition = addTable(pdf, nodeHeaders, nodeRows, yPosition, pageWidth);
      yPosition += 10;

      // Sections Table
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(26, 76, 58);
      pdf.text(`Tabella Sezioni ${scenarioLabel}`, 15, yPosition);
      yPosition += 8;

      const sectionHeaders = ['Nome sezione', 'Tipo intersezione', 'LOS', 'Veicoli (veh)', 'Ritardo (sec)', 'Ampiezza'];
      const sectionRows = [];

      scenarioData.LosNodi.forEach(node => {
        if (node.sezioni) {
          const nodeType = node.tipo ?? 'N/A';
          Object.values(node.sezioni).slice(0, 3).forEach(section => {
            sectionRows.push([
              (section.nomeSec ?? 'N/A').substring(0, 20),
              nodeType.substring(0, 12),
              section.losSec ?? 'N/A',
              section.count !== undefined && section.count !== null ? String(section.count) : 'N/A',
              section.delaySec !== undefined && section.delaySec !== null ? section.delaySec.toFixed(1) : 'N/A',
              section.ampiezza !== undefined && section.ampiezza !== null ? section.ampiezza.toFixed(1) : 'N/A'
            ]);
          });
        }
      });

      yPosition = addTable(pdf, sectionHeaders, sectionRows.slice(0, 15), yPosition, pageWidth);
      yPosition += 10;

      return yPosition;
    };

    // Nodes and Sections Tables
    if (losDataJson?.scenario) {
      const scenarioData1 = losDataJson.scenario.find(s => s.replicaAvg === scenarioId1);

      if (scenarioData1) {
        yPosition = addScenarioTables(scenarioData1, 'Scenario 1', 1);
      }

      // Add Scenario 2 tables if in comparison mode
      if (isComparison) {
        const scenarioData2 = losDataJson.scenario.find(s => s.replicaAvg === scenarioId2);

        if (scenarioData2) {
          yPosition = addScenarioTables(scenarioData2, 'Scenario 2', 2);
        }
      }
    }

    // Add map screenshot (only screenshot in the report)
    try {
      const mapContainer = document.querySelector('.confronto-map-wrapper');
      if (mapContainer) {
        // Always add a new page for the map view
        pdf.addPage();
        yPosition = 20;

        pdf.setFontSize(14);
        pdf.setTextColor(26, 76, 58);
        pdf.text('Vista Mappa', 15, yPosition);
        yPosition += 10;

        // Wait for maps and tiles to fully render
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Capture exactly what the user sees - no modifications
        const canvas = await html2canvas(mapContainer, {
          useCORS: true,
          allowTaint: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgWidth = pageWidth - 30;
        // Allow larger height for better visibility - almost full page
        const imgHeight = Math.min((canvas.height * imgWidth) / canvas.width, pageHeight - yPosition - 15);

        // Center the image horizontally
        const imgX = (pageWidth - imgWidth) / 2;

        pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight, undefined, 'FAST');
      }
    } catch (error) {
      console.error('Error capturing map:', error);
    }

    pdf.save(`scenario-report-${scenarioId1}-${date}.pdf`);
    return;
  }

  // SELECTION REPORT - Selected node/road with charts drawn natively
  if (reportType === 'selection') {
    pdf.setFontSize(16);
    pdf.setTextColor(26, 76, 58);
    pdf.text('Rapporto Selezione Corrente', 15, yPosition);
    yPosition += 12;

    // Helper to get sistema values for selection report
    const getSelectionSistemaValues = (scenarioId) => {
      if (!graficiDataJson?.scenario) return null;
      const scenario = graficiDataJson.scenario.find(s => s.replicaAvg === scenarioId);
      return scenario?.sistema || null;
    };

    // Add node data if available
    const addSelectionData = (nodeData, roadData, scenarioNum, metrics, chartData, scenarioId) => {
      if (!nodeData && !roadData) return yPosition;

      // Get sistema values for this scenario
      const sistema = getSelectionSistemaValues(scenarioId);

      // Section header
      pdf.setFontSize(14);
      pdf.setTextColor(26, 76, 58);
      pdf.text(`Scenario ${scenarioNum}`, 15, yPosition);
      yPosition += 10;

      // Add global scenario metrics from sistema
      pdf.setFontSize(12);
      pdf.setTextColor(26, 76, 58);
      pdf.text('Dati Globali Scenario',15, yPosition);
      yPosition += 6;

      const globalHeaders = ['Metrica', 'Valore'];
      const globalRows = [
        ['Flusso (Veic/H)', sistema?.flowSys !== undefined && sistema?.flowSys !== null ? String(sistema.flowSys.toFixed(2)) : 'N/A'],
        ['Coda Media (Veic)', sistema?.qmeanSys !== undefined && sistema?.qmeanSys !== null ? String(sistema.qmeanSys.toFixed(2)) : 'N/A'],
        ['Ritardo Medio (Sec/Km)', sistema?.dtimeSys !== undefined && sistema?.dtimeSys !== null ? String(sistema.dtimeSys.toFixed(2)) : 'N/A']
      ];

      yPosition = addTable(pdf, globalHeaders, globalRows, yPosition, pageWidth);
      yPosition += 10;

      // Calculate total vehicles from all sections
      let totalVehicles = 0;
      if (nodeData?.sezioni) {
        Object.values(nodeData.sezioni).forEach(sec => {
          totalVehicles += sec.count ?? 0;
        });
      }

      // If section is selected, show section-specific data
      if (roadData?.sectionData) {
        pdf.setFontSize(12);
        pdf.setTextColor(26, 76, 58);
        pdf.text('Dati Sezione Selezionata', 15, yPosition);
        yPosition += 6;

        const sectionHeaders = ['Metrica', 'Valore'];
        const sectionRows = [
          ['Nome Sezione', roadData.sectionData.nomeSec ?? 'N/A'],
          ['LOS', roadData.sectionData.losSec ?? 'N/A'],
          ['Veicoli (veh)', roadData.sectionData.count !== undefined && roadData.sectionData.count !== null ? String(roadData.sectionData.count) : 'N/A'],
          ['Ritardo (sec)', roadData.sectionData.delaySec !== undefined && roadData.sectionData.delaySec !== null ? roadData.sectionData.delaySec.toFixed(1) : 'N/A'],
          ['Ampiezza', roadData.sectionData.ampiezza !== undefined && roadData.sectionData.ampiezza !== null ? roadData.sectionData.ampiezza.toFixed(1) : 'N/A']
        ];

        yPosition = addTable(pdf, sectionHeaders, sectionRows, yPosition, pageWidth);
        yPosition += 10;

        // Draw charts for this section
        if (chartData && (chartData.veicoli?.length || chartData.codaMax?.length || chartData.tempo?.length || chartData.velocita?.length)) {
          if (yPosition > pageHeight - 130) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(12);
          pdf.setTextColor(26, 76, 58);
          pdf.text('Grafici Analisi Sezione', 15, yPosition);
          yPosition += 8;

          yPosition = drawChartsGrid(pdf, chartData, yPosition, pageWidth, pageHeight);
        }
      }
      // If node is selected, show node info
      else if (nodeData) {
        // Info box - increased height to fit vehicles
        pdf.setFillColor(248, 248, 248);
        pdf.roundedRect(15, yPosition - 3, pageWidth - 30, 42, 3, 3, 'F');

        pdf.setFontSize(10);
        pdf.setTextColor(51, 51, 51);

        pdf.setFont(undefined, 'bold');
        pdf.text('Nodo:', 20, yPosition + 5);
        pdf.setFont(undefined, 'normal');
        pdf.text(nodeData.nome || 'Intersezione', 35, yPosition + 5);

        pdf.setFont(undefined, 'bold');
        pdf.text('Tipo:', 20, yPosition + 12);
        pdf.setFont(undefined, 'normal');
        pdf.text(nodeData.tipo || 'N/A', 35, yPosition + 12);

        pdf.setFont(undefined, 'bold');
        pdf.text('LOS:', 20, yPosition + 19);
        pdf.setFont(undefined, 'normal');
        pdf.text(nodeData.losNode || 'N/A', 35, yPosition + 19);

        pdf.setFont(undefined, 'bold');
        pdf.text('Ritardo:', 20, yPosition + 26);
        pdf.setFont(undefined, 'normal');
        pdf.text(`${nodeData.delayNode ? nodeData.delayNode.toFixed(2) : 'N/A'} sec`, 42, yPosition + 26);

        // Add total vehicles
        pdf.setFont(undefined, 'bold');
        pdf.text('Veicoli:', 20, yPosition + 33);
        pdf.setFont(undefined, 'normal');
        pdf.text(`${totalVehicles} veh`, 42, yPosition + 33);

        yPosition += 49;

        // Add sections table if nodeData has sections
        if (nodeData?.sezioni && Object.keys(nodeData.sezioni).length > 0) {
          pdf.setFontSize(12);
          pdf.setTextColor(26, 76, 58);
          pdf.text('Sezioni Entranti nell\'Intersezione:', 15, yPosition);
          yPosition += 8;

          const sectionHeaders = ['Nome Sezione', 'LOS', 'Ritardo (sec)', 'Veicoli (veh)', 'Ampiezza'];
          const sectionRows = [];

          Object.values(nodeData.sezioni).forEach(section => {
            sectionRows.push([
              (section.nomeSec ?? 'N/A').substring(0, 20),
              section.losSec ?? 'N/A',
              section.delaySec !== undefined && section.delaySec !== null ? section.delaySec.toFixed(1) : 'N/A',
              section.count !== undefined && section.count !== null ? String(section.count) : 'N/A',
              section.ampiezza !== undefined && section.ampiezza !== null ? String(section.ampiezza) : 'N/A'
            ]);
          });

          yPosition = addTable(pdf, sectionHeaders, sectionRows, yPosition, pageWidth);
          yPosition += 5;
        }
      }

      return yPosition + 5;
    };

    yPosition = addSelectionData(nodeData1, roadData1, 1, nodeMetrics1, chartData1, scenarioId1);

    if (isComparison && (nodeData2 || roadData2)) {
      yPosition = addSelectionData(nodeData2, roadData2, 2, nodeMetrics2, chartData2, scenarioId2);
    }

    // Add map screenshot (only screenshot needed)
    try {
      const mapContainer = document.querySelector('.confronto-map-wrapper');
      if (mapContainer) {
        // Check if we need a new page for the map
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(26, 76, 58);
        pdf.text('Vista Mappa', 15, yPosition);
        yPosition += 10;

        // Wait for maps and tiles to fully render
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Capture exactly what the user sees - no modifications
        const canvas = await html2canvas(mapContainer, {
          useCORS: true,
          allowTaint: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgWidth = pageWidth - 30;
        const imgHeight = Math.min((canvas.height * imgWidth) / canvas.width, pageHeight - yPosition - 15);

        // Center the image horizontally
        const imgX = (pageWidth - imgWidth) / 2;

        pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight, undefined, 'FAST');
      }
    } catch (error) {
      console.error('Error capturing map:', error);
    }

    pdf.save(`selection-report-${date}.pdf`);
    return;
  }

  // Default/fallback - simple report
  pdf.setFontSize(12);
  pdf.setTextColor(51, 51, 51);
  pdf.text('Report generated successfully.', 15, yPosition);

  pdf.save(`traffic-report-${date}.pdf`);
};
